import Workspace from 'iam/models/Workspace'
import { action, computed, makeObservable, observable } from 'mobx'
import BundleSearchViewModel from './BundleSearchViewModel'
import groupBy from 'lodash/groupBy'
import { Bundle } from './bundle'
import EditBundleViewModel from './views/EditBundleDialog/EditBundleViewModel'
import ArchiveBundleViewModel from './views/ArchiveBundleModal/ArchiveBundleViewModel'
import UnarchiveBundleViewModel from './views/UnarchiveBundleModal/UnarchiveBundleViewModel'
import DuplicateBundleViewModel from './views/DuplicateBundleDialog/DuplicateBundleViewModel'
import { Id } from '../../types/definitions'
import { BulkDuplicateBundleViewModel } from './views/BulkDuplicateBundleDialog/BulkDuplicateBundleViewModel'
import { BulkArchiveBundleViewModel } from './views/BulkArchiveBundleDialog/BulkArchiveBundleViewModel'
import { BulkEditPriceViewModel } from './views/BulkEditPriceDialog/BulkEditPriceViewModel'
import { BulkApplyDiscountViewModel } from './views/BulkApplyDiscountDialog/BulkApplyDiscountViewModel'

export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',
  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',
  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

export default class WorkspaceBundleViewModel {
  @observable
  selectedBundlesId = new Set<Id>()

  constructor(
    private workspace: Workspace,
    private searcher: BundleSearchViewModel,
    private isWorkspaceSelected: () => boolean,
    public editBundleViewModel: EditBundleViewModel,
    public archiveBundleViewModel: ArchiveBundleViewModel,
    public unarchiveBundleViewModel: UnarchiveBundleViewModel,
    public duplicateBundleViewModel: DuplicateBundleViewModel,
    private bulkDuplicateBundleViewModel: BulkDuplicateBundleViewModel,
    private bulkArchiveBundleViewModel: BulkArchiveBundleViewModel,
    private bulkEditPriceViewModel: BulkEditPriceViewModel,
    private bulkApplyDiscountViewModel: BulkApplyDiscountViewModel,
  ) {
    makeObservable(this)
  }

  @computed
  get id() {
    return this.workspace.id
  }

  @computed
  get isFilteredByWorkspace() {
    return this.isWorkspaceSelected()
  }

  /**
   * Used to know if there are bundles in the workspace
   **/
  @computed
  get hasBundles() {
    return this.searcher.allBundles.some(
      (b) => b.workspace.id === this.workspace.id,
    )
  }

  /**
   * get all bundles in the workspace
   **/
  @computed
  get bundles() {
    return this.searcher.allBundles
      .filter((b) => b.workspace.id === this.workspace.id)
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * get the bundle in the workspace grouped by
   * bundles that contains archived items and bundles without archived items
   **/
  @computed
  get bundlesGroupedByHavingArchivedItems() {
    return groupBy(this.bundles, (bundle: Bundle) =>
      bundle.archivedItemsCount > 0 ? 'hasArchivedItems' : 'allActive',
    )
  }

  @computed
  get showBulkActions() {
    return this.selectedBundlesId.size > 0
  }

  @computed
  get selectedBundles() {
    return this.bundles.filter((bundle) =>
      this.selectedBundlesId.has(bundle.Id),
    )
  }

  @computed
  get selectedState() {
    if (this.selectedBundlesId.size === 0) {
      return SelectionState.NoneSelected
    } else if (this.bundles.length === this.selectedBundlesId.size) {
      return SelectionState.AllSelected
    } else {
      return SelectionState.SomeSelected
    }
  }

  isSelected = (bundleId: Id) => this.selectedBundlesId.has(bundleId)

  @action.bound
  toggleBundle(checked: boolean, bundleId: Id) {
    if (checked) {
      this.selectedBundlesId.add(bundleId)
    } else {
      this.selectedBundlesId.delete(bundleId)
    }
  }

  @action.bound
  toggleAll() {
    switch (this.selectedState) {
      case SelectionState.AllSelected:
        this.selectedBundlesId.clear()
        break
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        this.selectedBundlesId = new Set(
          this.bundles.map((bundle) => bundle.Id),
        )
        break
    }
  }

  @action.bound
  clearSelectedBundle() {
    this.selectedBundlesId.clear()
  }

  bulkDuplicate() {
    const selectedBundles = this.selectedBundles

    if (selectedBundles.length === 1) {
      return this.duplicateBundleViewModel.open(selectedBundles[0])
    }

    this.bulkDuplicateBundleViewModel.open(selectedBundles)
  }

  bulkArchive() {
    this.bulkArchiveBundleViewModel.open(this.selectedBundles)
  }

  bulkEditPrice() {
    this.bulkEditPriceViewModel.open(this.selectedBundles)
  }

  bulkApplyDiscount() {
    this.bulkApplyDiscountViewModel.open(this.selectedBundles)
  }
}
